<script setup>
import AnButton from "@/components/common/AnButton.vue";
import NewsSection from "@/sections/dashboard/NewsSection.vue";
import PortfolioCharts from "@/sections/dashboard/PortfolioCharts.vue";
import PortfolioTable from "@/sections/dashboard/PortfolioTable.vue";
import { useUserStore } from "@/store/user-store";
import { computed, ref } from "vue";
import Opportunities from "@/sections/dashboard/Opportunities.vue";
import AnLoader from "@/components/common/AnLoader.vue";
import { useRouter } from "vue-router";
import FinancialInformationWizard from "@/components/dashboard/FinancialInformationWizard.vue";
import TopDashboardCards from "@/components/dashboard/TopDashboardCards.vue";

// Define props
const props = defineProps({
  data: Object,
  isLoading: Boolean,
  showPortfolioCommentary: Boolean
});

// Define emits
const emit = defineEmits(['open-recommendation', 'refresh-dashboard', 'open-account-aggregator']);

const store = useUserStore();

const data = computed(() => props.data || store.getDashboardData || null);
const router = useRouter();

// Add wizard state for financial information
const showFinancialWizard = ref(false);

// Check if financial statement is empty/null
const hasFinancialData = computed(() => {
  const fs = store.financialStatement;
  if (!fs) return false;

  // Check if key financial data exists
  const hasIncome = fs.customerIncome && fs.customerIncome.length > 0;
  const hasExpenses = fs.customerExpenses && fs.customerExpenses.length > 0;
  const hasLiabilities = fs.customerLiabilities && fs.customerLiabilities.length > 0;

  return hasIncome || hasExpenses || hasLiabilities;
});

// Check if portfolio is effectively empty (all values are zero)
const isPortfolioEmpty = computed(() => {
  if (!data.value) return true;

  const summary = data.value.portfolioDailySummary?.[0];
  if (!summary) return true;

  return summary.portfolioTotalMarketVal === 0 &&
         summary.stockPFMarketVal === 0 &&
         summary.mutualFundPFMarketVal === 0 &&
         summary.otherTMV === 0;
});

// Navigate to add holdings screen
const navigateToAddHoldings = () => {
  router.push('/dashboard/add-holdings');
};

const openFinancialWizard = () => {
  showFinancialWizard.value = true;
};
</script>

<template>
  <div>
    <AnLoader v-if="store.isDashboardLoading" />
    <div v-else-if="!data">There is some issue</div>
    <div v-else>
      <!-- CTA Banner for Missing Financial Information (Independent of portfolio state) -->
      <div v-if="!hasFinancialData" class="financial-cta-banner">
        <div class="cta-icon">
          <i class="pi pi-info-circle"></i>
        </div>
        <div class="cta-content">
          <h3 class="cta-title">Complete Your Financial Profile</h3>
          <p class="cta-description">
            As your SEBI-registered Investment Adviser, we need comprehensive insights into your financial situation
            to provide personalized, compliant investment recommendations. Your income, expenses, savings, and insurance
            details enable us to craft strategies aligned with your unique circumstances and goals.
          </p>
          <button class="cta-button" @click="openFinancialWizard">
            <i class="pi pi-pencil"></i>
            Provide Financial Information
          </button>
        </div>
      </div>

      <!-- Top Dashboard Cards - Always show (for goal reminders) -->
      <TopDashboardCards />

      <!-- Existing Portfolio Logic (unchanged) -->
      <!-- Show simple centered empty state for portfolios with no holdings OR all zero values -->
      <div
        v-if="(data?.stocksOwned?.length === 0 && (!data?.portfolioDailySummary || data?.portfolioDailySummary?.length === 0)) || isPortfolioEmpty"
        class="d-flex flex-direction-column align-items-center gap-20 flex-1 justify-center"
      >
        <img src="@/assets/images/no-data.png" class="image" />
        <div
          class="d-flex flex-direction-column align-items-center font-20 font-weight-500"
        >
          <span> Track all your investments here.</span>
          <span>Add your holdings to see real-time insights! </span>
          <div class="empty-state-actions">
            <AnButton label="Add Holding" variant="primary" @click="navigateToAddHoldings" />
            <button
              class="action-btn holdings-btn"
              @click="emit('open-account-aggregator')"
            >
              <i class="pi pi-download"></i>
              Get Existing Holdings
            </button>
            <button
              class="action-btn refresh-btn"
              @click="emit('refresh-dashboard')"
              :disabled="isLoading"
            >
              <i class="pi pi-refresh" :class="{ 'pi-spin': isLoading }"></i>
              Refresh Dashboard
            </button>
          </div>
        </div>
      </div>

      <!-- Portfolio with data -->
      <div v-else>


        <!-- Only show charts if portfolio has meaningful data -->
        <PortfolioCharts
          v-if="!isPortfolioEmpty"
          :fixedIncome="data?.fixedIncome || []"
          :stocks="data?.stocksOwned || []"
          :mutualFunds="data?.mutualFunds || []"
          :otherInvestment="data?.otherInvest || []"
          :summary="data?.portfolioDailySummary || []"
        />

        <!-- Portfolio Header with Action Buttons - AFTER charts -->
        <div class="portfolio-header">
          <h2 class="portfolio-title">My Portfolio Securities</h2>
          <div class="portfolio-actions">
            <button
              class="action-btn refresh-btn"
              @click="emit('refresh-dashboard')"
              :disabled="isLoading"
            >
              <i class="pi pi-refresh" :class="{ 'pi-spin': isLoading }"></i>
              Refresh Dashboard
            </button>

            <button
              class="action-btn holdings-btn"
              @click="emit('open-account-aggregator')"
            >
              <i class="pi pi-download"></i>
              Get Existing Holdings
            </button>
          </div>
        </div>

        <PortfolioTable
          :stocks="data?.stocksOwned || []"
          :mutualFunds="data?.mutualFunds || []"
          :fixedIncome="data?.fixedIncome || []"
          :otherInvestments="data?.otherInvest || []"
          :dob="data?.dob || null"
          :portfolioSummary="data?.portfolioDailySummary?.[0] || null"
        />

        <!-- Only show other sections if portfolio has meaningful data -->
        <template v-if="!isPortfolioEmpty">
          <Opportunities />
          <NewsSection :news="Array.isArray(data.stockNews) ? data.stockNews : []" />
        </template>
      </div>

      <!-- Financial Information Wizard Dialog (Always available when data exists) -->
      <FinancialInformationWizard v-model="showFinancialWizard" />
    </div>
  </div>
</template>

<style scoped>
.image {
  height: 176px;
  width: 184px;
}

.empty-state-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.portfolio-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  margin-bottom: 20px;
  padding-top: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.portfolio-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.portfolio-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: 36px;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn:active {
  transform: translateY(0);
}

.action-btn i {
  font-size: 14px;
}

.refresh-btn {
  background: #10b981;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.refresh-btn:hover {
  background: #059669;
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.3);
}

.refresh-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.refresh-btn:disabled:hover {
  background: #9ca3af;
  transform: none;
  box-shadow: none;
}

.holdings-btn {
  background: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.holdings-btn:hover {
  background: #2563eb;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.commentary-btn {
  background: var(--an-primary, #0d7c68);
  box-shadow: 0 2px 4px rgba(13, 124, 104, 0.2);
}

.commentary-btn:hover {
  background: var(--an-primary-dark, #0a6454);
  box-shadow: 0 4px 8px rgba(13, 124, 104, 0.3);
}

/* CTA Banner Styles */
.financial-cta-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 24px;
  margin-bottom: 30px;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.1);
}

.cta-icon {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.cta-icon i {
  font-size: 1.5rem;
  color: white;
}

.cta-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cta-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0;
}

.cta-description {
  font-size: 0.95rem;
  color: #1e40af;
  line-height: 1.5;
  margin: 0;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--an-primary, #0d7c68);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  box-shadow: 0 2px 8px rgba(13, 124, 104, 0.3);
}

.cta-button:hover {
  background: var(--an-primary-dark, #0a6454);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 124, 104, 0.4);
}

.cta-button:active {
  transform: translateY(0);
}

.cta-button i {
  font-size: 1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .portfolio-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .portfolio-actions {
    justify-content: center;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }

  .financial-cta-banner {
    flex-direction: column;
    padding: 20px;
    gap: 16px;
  }

  .cta-icon {
    width: 50px;
    height: 50px;
    align-self: center;
  }

  .cta-icon i {
    font-size: 1.5rem;
  }

  .cta-title {
    font-size: 1.2rem;
    text-align: center;
  }

  .cta-description {
    font-size: 0.9rem;
    text-align: center;
  }

  .cta-button {
    align-self: stretch;
    justify-content: center;
  }
}
</style>
